/**
 * 报名活动（后端 /manager/sign）的展示口径。
 *
 * 枚举取值对应后端 src/common/contants/index.contants.ts：
 * SIGN_STATUS 0待支付 1待审核 2报名成功 3审核失败 4已关闭，
 * STORE_ORM_TYPE 1文本 2多行文本 3电话 4数字 5身份证 6单选 7多选 8时间预约。
 *
 * 字段类型那份和 views/shop/wechat/index/page/dialog/form-type.ts 是同一套后端枚举，
 * 但那个文件在 views 下、common 不能反向依赖，所以这里单独放一份给 sign 用，互不影响。
 *
 * 后端把 tinyint 传成过字符串（element-plus 那套同理），所以下面一律先 Number() 再比较，
 * 不要用 ===。
 */

export const SIGN_STATUS_LIST = [
  { value: 0, name: "待支付" },
  { value: 1, name: "待审核" },
  { value: 2, name: "报名成功" },
  { value: 3, name: "审核失败" },
  { value: 4, name: "已关闭" },
];

export const FORM_TYPE_LIST = [
  { value: 1, name: "普通文本" },
  { value: 2, name: "多行文本" },
  { value: 3, name: "电话带区号" },
  { value: 4, name: "数字文本" },
  { value: 5, name: "身份证" },
  { value: 6, name: "单选" },
  { value: 7, name: "多选" },
  { value: 8, name: "时间预约" },
];

/** 单选 / 多选：content 存 { list: [{ name, value }] } */
export const FORM_OPTION_TYPES = [6, 7];

/**
 * 时间预约：content 存
 * { weekDays: [0..6], startTime: 'HH:mm', endTime: 'HH:mm', interval: 分钟 }
 * weekDays 取值同 JS getDay()：1=周一 … 6=周六、0=周日
 * interval 是 startTime ~ endTime 之间生成时段的间隔，单位分钟
 *
 * 后端 SignRowDto 对 8 的注释只写了 startTime/endTime，那是过时的：服务端 buildContent
 * 落到 default 分支原样存字符串，不校验 content，真正按上面这个形状渲染的是客户端。
 */
export const FORM_DATE_TYPE = 8;

/** 时间预约默认间隔（分钟） */
export const DEFAULT_INTERVAL = 30;

/** 周一 ~ 周日，1=周一 0=周日 */
export const FORM_WEEK_LIST = [
  { value: 1, name: "周一" },
  { value: 2, name: "周二" },
  { value: 3, name: "周三" },
  { value: 4, name: "周四" },
  { value: 5, name: "周五" },
  { value: 6, name: "周六" },
  { value: 0, name: "周日" },
];

export function getFormTypeName(type: any) {
  let item = FORM_TYPE_LIST.find((item) => item.value == type);
  return item ? item.name : "-";
}

export function isOptionType(type: any) {
  return FORM_OPTION_TYPES.some((item) => item == type);
}

export function getSignStatusText(status: any) {
  let item = SIGN_STATUS_LIST.find((item) => item.value == status);
  return item ? item.name : "-";
}

/** 待支付和待审核都还没结果，共用橙色；成功绿、失败红、关闭灰 */
export function getSignStatusClass(status: any) {
  switch (Number(status)) {
    case 0:
    case 1:
      return "status-pending";
    case 2:
      return "status-on";
    case 3:
      return "status-reject";
    case 4:
      return "status-off";
  }
  return "";
}

/** 只有待审核能通过/驳回（后端 sign-check 会校验状态，前端先挡一道） */
export function canCheck(status: any) {
  return Number(status) === 1;
}

/** 待支付、待审核能手动关闭（后端 sign-close 只认这两个状态） */
export function canClose(status: any) {
  let value = Number(status);
  return value === 0 || value === 1;
}

/**
 * 一条报名记录的填写内容：itemList[].content 是 { '表头id': 值 }，
 * 一次报名可以有多条 item（一份资料一条），各带一部分答案，先合并成一张表，
 * 再按活动的 activityRowList 取标签。表的 key 是表头 id 的字符串形式。
 */
export function buildAnswerMap(itemList: any[]) {
  let map: Record<string, any> = {};
  (itemList || []).forEach((item) => {
    Object.assign(map, item && item.content ? item.content : {});
  });
  return map;
}

/**
 * 单个答案的展示值：
 * 1/2/3/5/8 存的是字符串原样，4 是数字；
 * 6 单选存选项的 value、7 多选存 value 数组，都要按表头的 content.list 翻成选项名。
 */
export function formatAnswer(row: any, value: any) {
  if (value === undefined || value === null || value === "") {
    return "-";
  }
  if (isOptionType(row?.type)) {
    let list = row?.content?.list || [];
    let nameOf = (item: any) => {
      let option = list.find((option: any) => option.value == item);
      return option ? option.name : `${item}`;
    };
    if (Array.isArray(value)) {
      return value.length > 0 ? value.map(nameOf).join("、") : "-";
    }
    return nameOf(value);
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join("、") : "-";
  }
  return `${value}`;
}
